import type { Ref, DeepReadonly } from 'vue'
import type { ModelView } from '@/entities/meeting'
import type { CameraData } from '@/entities/model3d'
import { DEFAULT_MODEL_TWEEN_DURATION } from '@/lib/three-d-resources/controls/viewerControls'
import type { LabelsManager } from '@/lib/three-d-resources/labels-manager'
import type { SceneConfig } from '@/lib/three-d-resources/scene-config'
import type { ThreeDResourceManager } from '@/lib/three-d-resources/three-d-resource-manager'
import type { World } from '@/lib/three-d-resources/world'
import type { Model } from '~/lib/three-d-resources/model'
import type { DrillDataSetManager } from '~/lib/three-d-resources/drill-data-set-manager'

let requestedCameraId: number | null = null
let requestedModelId: number | null = null

export default function setScene({
  world,
  labelsManager,
  drillDataSetManager,
  currentSceneConfig,
  threeDResourceManager,
  priorityCameras,
  currentView,
  onSetScene,
  setSearch,
}: {
  world: World
  labelsManager: LabelsManager
  drillDataSetManager: DrillDataSetManager
  currentSceneConfig: Ref<SceneConfig | null>
  threeDResourceManager: ThreeDResourceManager
  priorityCameras: Ref<DeepReadonly<CameraData[]>>
  currentView?: Ref<DeepReadonly<ModelView>>
  onSetScene?: (params: { sceneConfig: SceneConfig }) => void
  setSearch: (search: string) => void
}) {
  return async function ({ modelId, cameraId, shouldTween }: { modelId: number; cameraId: number | null; shouldTween: boolean }) {
    setSearch('')
    requestedModelId = modelId
    requestedCameraId = cameraId

    const isInitialSetActiveScene = !currentSceneConfig.value

    const model = await threeDResourceManager.getModel(modelId)
    const sceneConfig = await threeDResourceManager.getSceneConfig(model, cameraId)

    _handleModelNotInPriorityCameras(modelId)

    if (requestedModelId !== model.id || requestedCameraId !== sceneConfig.id) return

    const hasModelChanged = world.currentModel?.id !== model.id
    if (hasModelChanged) {
      _setModel(model)
    }

    const isSceneConfigChanged = currentSceneConfig.value?.id !== sceneConfig.id
    if (isSceneConfigChanged) {
      world.setSceneConfig(sceneConfig)
    }

    const switchingFromExistingModel = world.currentModel && world.currentModel.id === modelId
    const tweenDuration = shouldTween && switchingFromExistingModel ? DEFAULT_MODEL_TWEEN_DURATION : 0

    world.controls.setCameraData(sceneConfig.cameraViewData, {
      currentState: isInitialSetActiveScene && currentView ? currentView.value : undefined,
      tweenDuration,
    })

    _setVisibility(model, sceneConfig, tweenDuration)

    if (hasModelChanged) {
      await drillDataSetManager.loadDrillDataSet({ companyId: model.apiData.company_id, modelId: model.id })
    }

    _setSceneAddons(sceneConfig)
    currentSceneConfig.value = sceneConfig
    onSetScene?.({ sceneConfig })
  }

  function _setSceneAddons(sceneConfig: SceneConfig) {
    labelsManager.setSceneConfig(sceneConfig)
    drillDataSetManager.setSceneId(sceneConfig.id)
  }

  function _setModel(model: Model) {
    world.setModel(model)
    labelsManager.toggleGroupVisibility(false) // Hide labels until the model is fully loaded
    labelsManager.fetchLabelsForModel(model.id, model.apiLayersById)
    labelsManager.setGlobalTextScale(model.getTextScale())
    drillDataSetManager.toggleGroupVisibility(false)
    world.controls.setMaxDistance(model.sceneSize)
  }

  function _setVisibility(model: Model, sceneConfig: SceneConfig, tweenDuration: number) {
    const nodeIdAndVisibility = []
    for (let [nodeId] of model.layerNodesById) {
      nodeIdAndVisibility.push({ nodeId, visible: sceneConfig.isVisible(nodeId) })
    }

    if (tweenDuration) {
      world.startLayerTransition(nodeIdAndVisibility)
    } else {
      world.setLayerVisibility(nodeIdAndVisibility)
    }
  }

  function _handleModelNotInPriorityCameras(modelId: number) {
    const isModelNotInPriorityCameras = !priorityCameras || !priorityCameras.value.find(({ model3d_id }) => model3d_id === modelId)

    if (isModelNotInPriorityCameras) {
      threeDResourceManager.loadResourcesForModelId(modelId)
    }
  }
}
